import React from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';


import { V2HeaderBar } from './header-bar';
import { QuickLinks } from './quick-links';
import { StatTiles } from './stat-tiles';
import { TodoPanel } from './todo-panel';
import { UpNextPanel } from './up-next-panel';
import { WorkOrderOverview } from './work-order-overview';

import { EnhancedRefreshControl } from '@/components/common/enhanced-refresh-control';
import { ApprovalInterstitial } from '@/components/dashboards/approval-interstitial';
import { Text, useTheme } from '@/components/ui';
import { useApi } from '@/hooks/use-api';
import { useIsLargeScreen } from '@/hooks/use-is-large-screen';
import { usePermissions } from '@/hooks/use-permissions';
import { usePullToRefresh } from '@/hooks/use-pull-to-refresh';

/**
 * The v2 Home screen (#279).
 *
 * Reads top to bottom in order of what someone opening it actually needs:
 *
 *   1. NEEDS YOUR ATTENTION — pending approvals, follow-ups. Things another person is
 *      blocked on, or a clock is running against. Self-hides entirely on a clear day.
 *   2. The four counts — a glance at the shape of the work.
 *   3. Quick Links + Work Order Overview beside To Do, side by side on a wide window. For
 *      maintenance staff To Do is replaced by Up Next — the ranked work to start on next —
 *      on every platform; every other role keeps To Do.
 *
 * That order is the design. The old Home led with "My Work Orders", a list you had to
 * read to discover whether anything was urgent; this answers that before you scroll. That
 * list is now the Work Orders page (`dashboards/default.tsx`, `dashboards/mobile.tsx`).
 */

export const V2Dashboard: React.FC = () => {
	const { user } = useApi();
	const theme = useTheme();
	const { refreshing, onRefresh } = usePullToRefresh();
	const isLargeScreen = useIsLargeScreen();
	const { isMaintenance } = usePermissions();

	const firstName = (user?.name ?? '').trim().split(/\s+/)[0];
	/**
	 * Wide is wide, on any platform: iPad landscape gets the same header and two columns as
	 * desktop web. Native's drawer screens hide their nav header at this width
	 * (`headerShown: !isLargeScreen` in app/index.tsx), so without this bar there is no search
	 * or create on Home at all. The two-column row still collapses on anything narrow, where
	 * the donut and the task list each want the full width.
	 */
	const showHeaderBar = Platform.OS === 'web' || isLargeScreen;
	const twoUp = isLargeScreen;

	return (
		<View style={styles.shell} testID="v2-dashboard">
			{/* Narrow native has a real navigation header with search in it instead. */}
			{showHeaderBar && <V2HeaderBar title="Dashboard" />}

			<ScrollView
				refreshControl={
					<EnhancedRefreshControl
						refreshing={refreshing}
						onRefresh={onRefresh}
						tintColor={theme.colors.primary}
					/>
				}
				contentContainerStyle={styles.page}
			>
				<View style={styles.hero}>
					<Text style={styles.heroTitle}>
						Welcome back{firstName ? `, ${firstName}` : ''}
					</Text>
					<Text style={styles.heroSub}>
						Here&apos;s what&apos;s happening with your properties today.
					</Text>
				</View>

				<StatTiles style={styles.block} />

				{/*
					LEFT: Quick Links with the donut tucked under it. RIGHT: To Do, spanning
					the full height of both.

					To Do is the column that grows — it is the only one whose length depends
					on how much work there is, and giving it the height of two cards means
					eight rows fit without the panel scrolling inside a scroll view. Quick
					Links and the donut are both fixed-size by nature, so stacking them
					wastes nothing.

					Quick Links leads because it is the thing people come here to CLICK; the
					donut is a read-only summary. On a narrow screen that ordering matters
					most — it stacks in source order, so the navigation lands above the fold
					instead of below a chart.
				*/}
				<View style={[styles.columns, !twoUp && styles.columnsStacked]} testID="v2-columns">
					<View style={twoUp ? styles.col : undefined}>
						<QuickLinks style={styles.block} />
						<WorkOrderOverview />
					</View>
					<View style={twoUp ? styles.col : undefined}>
						{/* flex:1 is what makes it span both cards on the left. */}
						{isMaintenance
							? <UpNextPanel style={twoUp ? styles.fill : undefined} />
							: <TodoPanel style={twoUp ? styles.fill : undefined} />}
					</View>
				</View>
			</ScrollView>

			{/*
				Blocking prompt, once per session, only for approvals already past their
				window. Mounted here rather than inside a widget so it does not depend on
				which cards happen to render.
			*/}
			<ApprovalInterstitial />
		</View>
	);
};

const styles = StyleSheet.create({
	shell: { flex: 1, backgroundColor: '#f5f6f8' },
	page: { padding: 20, paddingBottom: 40 },
	hero: { marginBottom: 20 },
	heroTitle: { fontSize: 28, fontWeight: '700', color: '#1a2027' },
	heroSub: { fontSize: 15, color: '#607d8b', marginTop: 4 },
	block: { marginBottom: 16 },
	// `alignItems: stretch` (the default) is what lets the To Do column match the height
	// of the stacked pair beside it.
	columns: { flexDirection: 'row', gap: 16, marginBottom: 16 },
	columnsStacked: { flexDirection: 'column' },
	fill: { flex: 1 },
	// minWidth 0 so a long legend label truncates instead of forcing the column wider —
	// RN-Web flex children default to min-width:auto.
	col: { flex: 1, minWidth: 0 },
});

export default V2Dashboard;
